import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect, useCallback } from 'react';
import { getAuthHeaders } from '../utils/authHeaders';
import { API, Badge, tableStyle, thStyle, tdStyle, inputStyle, btnPrimary, btnSecondary, btnSmall, overlayStyle, modalStyle, FormRow, fmtQty } from './InventoryShared';

export default function InventoryPicking() {
  const companyId = localStorage.getItem('selectedCompanyId');
  const [list, setList] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [products, setProducts] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [status, setStatus] = useState('');
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/companies/${companyId}/inventory/pickings?per_page=50`;
      if (status) url += `&status=${status}`;
      const res = await fetch(url, { headers: getAuthHeaders() });
      const json = await res.json();
      setList(Array.isArray(json.data) ? json.data : []);
    } catch (e) { console.error(e); setList([]); }
    setLoading(false);
  }, [companyId, status]);

  const fetchAux = useCallback(async () => {
    const [wRes, pRes, rRes] = await Promise.all([
      fetch(`${API}/companies/${companyId}/inventory/warehouses`, { headers: getAuthHeaders() }),
      fetch(`${API}/companies/${companyId}/inventory/products?per_page=200`, { headers: getAuthHeaders() }),
      fetch(`${API}/companies/${companyId}/inventory/reservations?status=active&per_page=100`, { headers: getAuthHeaders() }),
    ]);
    setWarehouses(await wRes.json());
    const pJson = await pRes.json();
    setProducts(Array.isArray(pJson.data) ? pJson.data : []);
    const rJson = await rRes.json();
    setReservations(Array.isArray(rJson.data) ? rJson.data : []);
  }, [companyId]);

  useEffect(() => { fetchList(); }, [fetchList]);
  useEffect(() => { fetchAux(); }, [fetchAux]);

  const openView = async (id) => {
    const res = await fetch(`${API}/companies/${companyId}/inventory/pickings/${id}`, { headers: getAuthHeaders() });
    setViewing(await res.json());
  };

  return (
    <div style={{ padding: '24px 32px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#1a1a2e' }}>📋 Picking & Fulfillment</h2>
        <button style={btnPrimary} onClick={() => setCreating(true)}>+ New Picking List</button>
      </div>

      <div style={{ marginBottom: 16 }}>
        <select style={{ ...inputStyle, width: 200 }} value={status} onChange={e => setStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead><tr>{['Picking #', 'Warehouse', 'Order #', 'Lines', 'Status', 'Actions'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
            <tbody>
              {initialLoading && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center' }}>Loading...</td></tr>}
              {!loading && list.length === 0 && <tr><td colSpan={6} style={{ ...tdStyle, textAlign: 'center', color: '#9ca3af' }}>No picking lists</td></tr>}
              {list.map(p => (
                <tr key={p.id}>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>
                    <a href="#v" onClick={e => { e.preventDefault(); openView(p.id); }} style={{ color: '#3b82f6' }}>{p.picking_number}</a>
                  </td>
                  <td style={tdStyle}>{p.warehouse?.name}</td>
                  <td style={tdStyle}>{p.order_number || '—'}</td>
                  <td style={tdStyle}>{p.lines_count ?? 0}</td>
                  <td style={tdStyle}><Badge value={p.status} /></td>
                  <td style={tdStyle}><button style={btnSmall} onClick={() => openView(p.id)}>Open</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {creating && (
        <CreatePickingModal companyId={companyId} warehouses={warehouses} products={products} reservations={reservations}
          onClose={() => setCreating(false)} onSaved={() => { setCreating(false); fetchList(); }} />
      )}
      {viewing && (
        <PickingDetail companyId={companyId} picking={viewing}
          onClose={() => { setViewing(null); fetchList(); }} onRefresh={() => openView(viewing.id)} />
      )}
    </div>
  );
}

function CreatePickingModal({ companyId, warehouses, products, reservations, onClose, onSaved }) {
  const [warehouseId, setWarehouseId] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [reservationIds, setReservationIds] = useState([]);
  const [lines, setLines] = useState([]);
  const [saving, setSaving] = useState(false);

  const toggleRes = (id) => setReservationIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
  const addLine = () => setLines([...lines, { product_id: '', quantity: '' }]);
  const updateLine = (i, field, val) => setLines(lines.map((l, idx) => idx === i ? { ...l, [field]: val } : l));
  const removeLine = (i) => setLines(lines.filter((_, idx) => idx !== i));

  const filteredRes = warehouseId ? reservations.filter(r => String(r.warehouse_id) === String(warehouseId)) : reservations;

  const save = async () => {
    if (!warehouseId) { alert('Warehouse is required'); return; }
    const validLines = lines.filter(l => l.product_id && l.quantity);
    if (reservationIds.length === 0 && validLines.length === 0) { alert('Select reservations or add lines'); return; }
    setSaving(true);
    const res = await fetch(`${API}/companies/${companyId}/inventory/pickings`, {
      method: 'POST', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ warehouse_id: warehouseId, order_number: orderNumber || null, reservation_ids: reservationIds, lines: validLines }),
    });
    if (!res.ok) { const j = await res.json().catch(() => ({})); alert(j.message || 'Save failed'); setSaving(false); return; }
    onSaved();
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 640 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>New Picking List</h3>
        <FormRow label="Warehouse *">
          <select style={inputStyle} value={warehouseId} onChange={e => setWarehouseId(e.target.value)}>
            <option value="">— Select —</option>
            {warehouses.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
          </select>
        </FormRow>
        <FormRow label="Order # (reference)"><input style={inputStyle} value={orderNumber} onChange={e => setOrderNumber(e.target.value)} /></FormRow>

        <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', margin: '10px 0 6px' }}>FROM ACTIVE RESERVATIONS</div>
        <div style={{ maxHeight: 140, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 6, padding: 8, marginBottom: 10 }}>
          {filteredRes.length === 0 && <div style={{ color: '#9ca3af', fontSize: 13 }}>No active reservations</div>}
          {filteredRes.map(r => (
            <label key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 4 }}>
              <input type="checkbox" checked={reservationIds.includes(r.id)} onChange={() => toggleRes(r.id)} />
              {r.product?.name} — Qty {fmtQty(r.quantity)} {r.order_number ? `(${r.order_number})` : ''}
            </label>
          ))}
        </div>

        <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', margin: '10px 0 6px' }}>AD HOC LINES</div>
        {lines.map((l, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <select style={{ ...inputStyle, flex: 2 }} value={l.product_id} onChange={e => updateLine(i, 'product_id', e.target.value)}>
              <option value="">— Product —</option>
              {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <input type="number" placeholder="Qty" style={{ ...inputStyle, flex: 1 }} value={l.quantity} onChange={e => updateLine(i, 'quantity', e.target.value)} />
            <button style={btnSmall} onClick={() => removeLine(i)}>✕</button>
          </div>
        ))}
        <button style={btnSecondary} onClick={addLine}>+ Add Line</button>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
          <button style={btnSecondary} onClick={onClose}>Cancel</button>
          <button style={btnPrimary} onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Create'}</button>
        </div>
      </div>
    </div>
  );
}

function PickingDetail({ companyId, picking, onClose, onRefresh }) {
  const [busy, setBusy] = useState(false);

  const updateLine = async (lineId, pickedQty) => {
    await fetch(`${API}/companies/${companyId}/inventory/pickings/${picking.id}/lines/${lineId}`, {
      method: 'PUT', headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' }, body: JSON.stringify({ picked_qty: pickedQty }),
    });
    onRefresh();
  };

  const act = async (action) => {
    setBusy(true);
    await fetch(`${API}/companies/${companyId}/inventory/pickings/${picking.id}/${action}`, { method: 'POST', headers: getAuthHeaders() });
    setBusy(false); onRefresh();
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 640 }} onClick={e => e.stopPropagation()}>
        <h3 style={{ marginTop: 0 }}>{picking.picking_number} <Badge value={picking.status} /></h3>
        <div style={{ marginBottom: 10 }}><b>Warehouse:</b> {picking.warehouse?.name}</div>
        <table style={tableStyle}>
          <thead><tr>{['Product', 'Ordered', 'Picked'].map(h => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
          <tbody>
            {(picking.lines || []).map(l => (
              <tr key={l.id}>
                <td style={tdStyle}>{l.product?.name || l.product_id}</td>
                <td style={tdStyle}>{fmtQty(l.ordered_qty)}</td>
                <td style={tdStyle}>
                  {picking.status === 'in_progress' ? (
                    <input type="number" style={{ ...inputStyle, width: 90 }} defaultValue={l.picked_qty ?? ''}
                      onBlur={e => e.target.value !== '' && updateLine(l.id, Number(e.target.value))} />
                  ) : (l.picked_qty ?? 0)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16, flexWrap: 'wrap' }}>
          {picking.status === 'pending' && <button style={btnPrimary} onClick={() => act('start')} disabled={busy}>Start Picking</button>}
          {picking.status === 'in_progress' && <button style={btnPrimary} onClick={() => act('complete')} disabled={busy}>Complete</button>}
          {['pending', 'in_progress'].includes(picking.status) && <button style={btnSecondary} onClick={() => act('cancel')} disabled={busy}>Cancel</button>}
          <button style={btnSecondary} onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
